import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  InputSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { SkeletonTextComponent } from '../skeleton-text/skeleton-text.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowTrendUp,
  faArrowTrendDown,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons';

export enum StatisticCardNotation {
  PERCENTAGE = 'PERCENTAGE',
  OF_TOTAL = 'OF_TOTAL',
  NONE = 'NONE',
}

@Component({
  selector: 'lyceo-statistic-card',
  templateUrl: './statistic-card.component.html',
  styleUrls: ['./statistic-card.component.scss'],
  imports: [CommonModule, SkeletonTextComponent, FontAwesomeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatisticCardComponent implements OnDestroy {
  public readonly StatisticCardNotation: typeof StatisticCardNotation =
    StatisticCardNotation;

  protected readonly faArrowTrendUp: IconDefinition = faArrowTrendUp;
  protected readonly faArrowTrendDown: IconDefinition = faArrowTrendDown;

  public readonly cardClick: OutputEmitterRef<void> = output<void>();

  /**
   * Optional title which will be set above or below the value.
   */
  public title: InputSignal<string | undefined> = input<string | undefined>();

  /**
   * Either place the title at the top or bottom of the statistic card.
   *
   * Note: This is only used when title is set.
   */
  public titlePlacement: InputSignal<'bottom' | 'top'> = input<
    'bottom' | 'top'
  >('top');

  /**
   * Optional prefix for the statistic value
   */
  public statisticPrefix: InputSignal<string | undefined> = input<
    string | undefined
  >();

  /**
   * Suffix for the statistic value.
   *
   * For example when the value is 1 and the statisticName is 'opdracht'
   * the card displays `1 opdracht`.
   *
   * Note: You need to set both this and {@see statisticNamePlural} for it to
   * work. If only one is set the parameter is ignored.
   */
  public statisticName: InputSignal<string | undefined> = input<
    string | undefined
  >();

  /**
   * Suffix for the statistic value. for example when the value is 25 and the
   * statisticNamePlural is 'opdrachten' the card display `25 opdrachten`
   *
   * Note: You need to set both this and {@see statisticName} for it to
   * work. If only one is set the parameter are ignored.
   */
  public statisticNamePlural: InputSignal<string | undefined> = input<
    string | undefined
  >();

  /**
   * The previous statistic value. When this is set the card calculates the
   * difference (or different percentage).
   *
   * Note: When this parameter is empty is doesn't show the chart icon and or
   * any differences.
   */
  public previousValue: InputSignal<number | undefined> = input<
    number | undefined
  >();

  /**
   * The value's decimal representation is specified by the `digitsInfo`
   * parameter, written in the following format:<br>
   *
   * ```
   * {minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}
   * ```
   *
   *  - `minIntegerDigits`:
   * The minimum number of integer digits before the decimal point.
   * Default is 1.
   *
   * - `minFractionDigits`:
   * The minimum number of digits after the decimal point.
   * Default is 0.
   *
   *  - `maxFractionDigits`:
   * The maximum number of digits after the decimal point.
   * Default is 0.
   *
   * If the formatted value is truncated it will be rounded using the "to-nearest" method:
   *
   * ```
   * {{3.6 | number: '1.0-0'}}
   * <!--will output '4'-->
   *
   * {{-3.6 | number:'1.0-0'}}
   * <!--will output '-4'-->

   * {{3.64736 | number:'1.0-3'}}
   * <!--will output '3.648'-->
   *
   * {{4 | number:'1.2-0'}}
   * <!--will output '4.00'-->
   * ```
   **/
  public digitsInfo: InputSignal<string> = input<string>('1.0-0');

  /**
   * The 'total' value to display.
   */
  public total: InputSignal<number | 'infinite'> = input<number | 'infinite'>(
    'infinite',
  );

  /**
   * Whether the percentage will be shown inside the statistic card.
   */
  public notation: InputSignal<StatisticCardNotation | string> = input<
    StatisticCardNotation | string
  >(StatisticCardNotation.OF_TOTAL);

  /**
   * If true, a button tag will be rendered and the card will be tappable.
   */
  public button: InputSignal<boolean> = input<boolean>(false);

  /**
   * Show a spinner and obfuscate the content of the card.
   */
  public loading: InputSignal<boolean> = input<boolean>(false);

  /**
   * Set the value of the internal observable for value on the statistic card.
   */
  public value: InputSignal<number | undefined | null> = input<
    number | undefined | null
  >();

  /**
   * Calculated value as an observable.
   */
  protected value$: Observable<number>;

  /**
   * Loading state as an observable, so the template doesn't go haywire.
   */
  protected loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  /**
   * Percentage filled.
   */
  protected differenceWithPrevious$: Observable<number>;

  /**
   * The internal value of the statistic card.
   */
  protected valueSubject$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);

  /**
   * Subject to clean-up subscriptions onDestroy.
   */
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor() {
    this.value$ = this.createValueObservable();
    this.differenceWithPrevious$ = this.createCalculateObservable();
    effect(() => this.valueSubject$.next(this.value() ?? 0));
    effect(() => this.loading$.next(this.loading()));
  }

  /**
   * @inheritDoc
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onCardClick(): void {
    if (this.button()) {
      // TODO: The 'emit' function requires a mandatory void argument
      this.cardClick.emit();
    }
  }

  /**
   * Create an observable for the mapping of the current value in the value
   * subject, so it only needs to calculate when the value changes.
   */
  private createValueObservable(): Observable<number> {
    return this.valueSubject$.pipe(
      map((value) => {
        const total: 'infinite' | number = this.total();

        if (total === 'infinite') {
          return Math.max(value, 0);
        }
        return Math.min(total, Math.max(value, 0));
      }),
    );
  }

  /**
   * Do some calculations to render a correct statistic card.
   */
  private createCalculateObservable(): Observable<number> {
    return this.valueSubject$.pipe(
      takeUntil(this.destroy$),
      map((value) => {
        const total: number =
          this.total() === 'infinite' ? 100 : (this.total() as number);

        if (total === 100) {
          value = Math.max(value, 0);
        } else {
          value = Math.min(total, Math.max(value, 0));
        }

        if (this.notation() === StatisticCardNotation.PERCENTAGE) {
          return ((value - (this.previousValue() || 0)) / total) * 100;
        }
        return value - (this.previousValue() || 0);
      }),
    );
  }
}
